<template>
  <collapse
    :value="!isDeleted"
    :transition-on-mount="animateCouponCollapse"
    name="coupon"
    style="height: unset"
  >
    <div
      class="coupon"
      v-observe-visibility="{
          callback: couponVisibilityChanged,
            intersection: {
              threshold: 0.1,
        },
      }"
    >
      <carousel
        :paginationSize="6"
        :paginationPadding="6"
        :perPage="1"
        :speed="200"
        pagination-enabled
        center-mode
        class="coupon_carousel"
        :navigate-to="carouselIndex"
      >
        <slide v-for="(img, n) in filterImages(coupon.images)" :key="n">
          <div>
            <img
              :src="img.url"
              class="image"
              name="couponimage"
            />
            <div class="image_overlay"></div>
          </div>
        </slide>
      </carousel>

      <div class="image_rounding"></div>

      <div style="display: flex; flex-display: row; justify-content: space-between">
        <h2 class="header mx2 mb0 mt2" name="coupontitle">
          <b>{{ coupon.title }}</b>
        </h2>

        <div class="price_wrapper" :class="{hidden: !displayPrices}">
          <h2
            v-if="!isNil(discount)"
            :style="priceFontSize(discount)"
            class="price"
            name="newprice"
          >
            {{ discount }}
          </h2>

          <h2
            v-if="!isNil(previousPrice)"
            class="previous_price"
          >
              <span :style="priceFontSize(previousPrice)" name="normalprice">
                {{ previousPrice }}
              </span>
          </h2>
        </div>
      </div>

      <div v-if="showStats">
        <div class="py2 px4">
          <hr/>
        </div>
        <div class="coupon_stats flex mx-1 mb2">
          <div class="grow column flex center_text py5">
            <span class="metric_number">{{ coupon.ctaCount }}</span>
            <span class="metric_text">{{ $t('cms.statistics.coupon.ClickCount') }}</span>
          </div>

          <div class="separator_vertical"></div>

          <div class="grow column flex center_text py5">
            <span class="metric_number">{{ coupon.timesSent }}</span>
            <span class="metric_text">{{ $t('cms.statistics.coupon.SentCount') }}</span>
          </div>
        </div>
      </div>

      <div class="py2 px4">
        <hr/>
      </div>

      <div class="mx2 text_field" v-html="coupon.text"/>
      <div>
        <div class="py2 px4">
          <hr/>
        </div>
        <div class="flex justify_center">
          <icon blue-blue-tick :size="14" class="mr1"></icon>
          <b style="font-size: 14px;">
            {{ coupon.partnerName ? $t('cms.view.Partnership') : $t('cms.view.CompanyName') }}
          </b>
        </div>
        <p v-if="coupon.partnerName" class="center_text">
          {{ coupon.partnerName }} {{ $t('cms.view.And') }} {{ coupon.campaign.organization.name }}
        </p>
        <p v-else class="center_text">{{ coupon.campaign.organization.name }}</p>
      </div>

      <div class="px4 mb0 mt3">
        <hr class="dashed"/>
      </div>

      <div class="counterdown_section pa2 slightly_yellow_background">
        <p>{{ $t('cms.view.ValidityLine1Text') }}</p>
        <p>{{ $t('cms.view.ValidityLine2Text') }}</p>
        <countdown :to="countdownToDate"/>
        <p>{{ $t('cms.view.RedeemText') }}</p>
        <p>{{ redeemCouponText }}</p>
      </div>

      <div class="slightly_yellow_background" style="margin-top: -1px">
        <a v-if="iframeLink" :href="mapsLink" class="maps_link"/>
        <iframe
          :src="iframeLink"
          class="iframe"
          frameborder="0"
        />
      </div>

      <div class="footer" v-show="isDeletingCoupon">
        <button
          class="save_button"
        >
          <icon whiteCancel class="mr2" @click.native.stop="isDeletingCoupon = false" />
          <span>{{ $t('cms.delete.ConfirmQuestion') }}</span>
        </button>

        <button
          class="orange box px4"
          style="border: unset"
          @click="confirmDelete"
        >
          <b class="mx2">{{ $t('cms.delete.Confirm') }}</b>
        </button>
      </div>

      <div class="footer" v-show="!isDeletingCoupon">
        <!-- Hide save/delete coupon on TodaysCoupons page -->
        <span v-if="!canDeleteCoupons && isSaved"></span>
        <button
          v-else-if="!isSaved"
          class="save_button"
          @click="saveCoupon"
        >
          <icon download class="mr2" />
          <b>{{ $t('cms.view.SaveCouponButton') }}</b>
        </button>

        <button
          v-else
          class="save_button"
          @click="deleteCoupon"
        >
          <icon whiteTrash class="mr2" />
          <b>{{ $t('cms.view.DeleteCouponButton') }}</b>
        </button>

        <a
          v-if="redeemIsLink"
          :href="makeUrl(coupon.url)"
          @click="registerRedeem(coupon.id)"
        >
          <button
            class="good_background white box px4"
            style="border: unset"
          >
            <b>{{ ctaType.buttonText }}</b>
          </button>
        </a>

        <button
          v-else
          class="good_background white box px4"
          style="border: unset"
          @click="openRedeemModal(coupon.id)"
        >
          <b>{{ ctaType.buttonText }}</b>
        </button>
      </div>

      <modal v-model="showModal">
        <coupon-cta :coupon="coupon"/>
      </modal>
    </div>
  </collapse>
</template>

<script>
import isNil from 'lodash/isNil';
import { ObserveVisibility } from 'vue-observe-visibility';
import Countdown from './campaigns/Countdown.vue';
import CouponCta from './cta/CouponCta.vue';
import CarouselMixin from '@/mixins/carousel_mixin';
import i18n from '@/plugins/i18n';
import store from '@/store';
import { log } from '@/plugins/debugging';
import { isIdGenerated } from '@/utils/id_utils';
import { toPrice } from '@/utils/numbers';

export default {
  name: 'coupon',
  mixins: [CarouselMixin],
  components: { Countdown, CouponCta },
  i18n,

  directives: {
    ObserveVisibility,
  },

  data() {
    return {
      isSaved: this.coupon.isSaved,
      isDeletingCoupon: false,
      isDeleted: false,
      showModal: false,
      displayPrices: false,
      isVisible: false,
      watchTime: 0,
    };
  },

  props: {
    deviceToken: { type: String, required: false, default: null },
    showStats: { type: Boolean, required: false, default: false },
    coupon: {
      type: Object,
      required: true,
      validator(coupon) {
        if (!coupon.campaign) {
          console.warn("Coupon.vue requires the coupon prop to contain 'campaign'");
          return false;
        }

        if (!coupon.campaign.id || isIdGenerated(coupon.campaign.id)) {
          // If campaign is not saved yet, we cant expect to props checked below to be there
          return true;
        }

        const {
          endDate, endTime, daysActive, organization,
        } = coupon.campaign;
        if (!(endDate && endTime)) {
          console.warn("Coupon.vue requires the coupon prop to contain 'campaign.endDate' and 'campaign.endTime'");
          return false;
        }

        if (!daysActive) {
          console.warn("Coupon.vue requires the coupon prop to contain 'campaign.daysActive'");
          return false;
        }

        if (!(organization && organization.address)) {
          console.warn("Coupon.vue requires the coupon prop to contain 'campaign.organization.address'");
          return false;
        }

        return true;
      },
    },
  },

  watch: {
    'coupon.isSaved': {
      handler(value) {
        this.isSaved = value;
      },
    },
  },

  mounted() {
    setTimeout(() => { this.displayPrices = true; }, 100);
  },

  created() {
    document.addEventListener('visibilitychange', this.documentFocusChanged);
    this.interval = setInterval(() => {
      if (this.isVisible) {
        this.watchTime = this.watchTime + 1;
      }
    }, 1000);
  },

  beforeDestroy() {
    document.removeEventListener('visibilitychange', this.documentFocusChanged);
    clearInterval(this.interval);
  },

  computed: {
    ctaType() {
      return Object.values(this.$CONSTANTS.couponCtaTypes)
        .find(({ value }) => value === this.coupon.cta);
    },

    redeemIsLink() {
      const types = this.$CONSTANTS.couponCtaTypes;
      const linkCTAs = [types.buy.value, types.info.value];
      return this.coupon.url && linkCTAs.includes(this.coupon.cta);
    },

    discount() {
      if (!isNil(this.coupon.newPrice)) {
        return toPrice(this.coupon.newPrice, this.coupon.campaign.organization.currencySymbol);
      }

      if (!isNil(this.coupon.discountPercentage)) {
        return `${this.coupon.discountPercentage}%`;
      }


      return toPrice(this.coupon.normalPrice, this.coupon.campaign.organization.currencySymbol);
    },

    previousPrice() {
      // if newPrice and discountPercentage are empty, normalPrice will be
      // displayed in the green circle, and this should be empty
      if (isNil(this.coupon.newPrice) && isNil(this.coupon.discountPercentage)) {
        return null;
      }
      return toPrice(this.coupon.normalPrice, this.coupon.campaign.organization.currencySymbol);
    },

    canDeleteCoupons() {
      return store.state.canDeleteCoupons;
    },

    animateCouponCollapse() {
      return store.state.animateCouponCollapse;
    },

    redeemCouponText() {
      try {
        return this.getRedeemCouponText(this.coupon.campaign);
      } catch (e) {
        return null;
      }
    },

    iframeLink() {
      try {
        return `https://www.google.com/maps/embed/v1/place?q=${this.getAddress()}&key=AIzaSyCAETgkAEo9sOUJ1ipXmobH2mw3dT7Con0`;
      } catch (e) {
        return null;
      }
    },

    mapsLink() {
      try {
        return `https://maps.google.com/?q=${this.getAddress()}`;
      } catch (e) {
        return null;
      }
    },

    countdownToDate() {
      try {
        const { endDate, endTime } = this.coupon.campaign;
        return new Date(`${endDate}T${endTime}`);
      } catch (e) {
        return null;
      }
    },
  },

  methods: {
    isNil,

    getAddress() {
      return this.coupon.partnerAddress || this.coupon.partnerPostCode ? `${this.coupon.partnerAddress} ${this.coupon.partnerPostCode}`
        : this.coupon.campaign.organization.address;
    },

    couponVisibilityChanged(isVisible) {
      this.isVisible = isVisible;
    },

    async documentFocusChanged() {
      // Don't do anything if we are on the preview page
      if (!this.deviceToken) {
        return;
      }
      if (document.visibilityState === 'hidden') {
        const mutation = this.$mutations.updateWatchTime;

        await this.$mutate(mutation, {
          input: {
            deviceToken: this.deviceToken,
            couponId: this.coupon.id,
            seconds: this.watchTime,
          },
        });
        this.watchTime = 0;
      }
    },

    priceFontSize(price) {
      let fontSize;
      const priceLength = price.toString().length;
      if (priceLength <= 3) {
        fontSize = 20;
      } else if (priceLength <= 5) {
        fontSize = 18;
      } else if (priceLength === 6) {
        fontSize = 15;
      } else fontSize = 12;
      return `font-size:${fontSize}px`;
    },


    filterImages(images) {
      return images.filter(({ url }) => !!url);
    },

    makeUrl(url) {
      const startsWithHttp = url.substring(0, 'http'.length) === 'http';
      return startsWithHttp ? url : `https://${url}`;
    },

    registerRedeem(couponId) {
      // Check if we are in a webview by checking device token
      if (this.deviceToken) {
        this.$mutate(this.$mutations.registerRedeem, { couponId });
      }
    },

    openRedeemModal(couponId) {
      this.showModal = true;
      this.registerRedeem(couponId);
    },

    deleteCoupon() {
      this.isDeletingCoupon = true;
    },

    async confirmDelete() {
      // Dont do anything if we are on preview page (not in WC)
      if (!this.deviceToken) {
        return;
      }

      this.isDeleted = true;
      const input = { deviceToken: this.deviceToken, couponId: this.coupon.id };
      await this.$mutate(this.$mutations.deleteCouponFromDevice, { input });
      this.$bus.$emit('deleted-coupon');
      this.$bus.$emit('update-webcomponent');
    },

    async saveCoupon() {
      // Dont do anything if we are on preview page (not in WC)
      if (!this.deviceToken) {
        return;
      }

      this.isSaved = true;
      const input = { deviceToken: this.deviceToken, couponId: this.coupon.id };
      const resp = await this.$mutate(this.$mutations.saveCouponToDevice, { input });
      log(JSON.stringify(resp));
      const { errors, data } = resp;
      const ok = data && data.saveCouponToDevice && data.saveCouponToDevice.ok;
      if (!ok || errors.length > 0) {
        log(`Saved coupons was not OK, errors: ${JSON.stringify(errors)}`);
        this.isSaved = false;
      }
      this.$bus.$emit('saved-coupon');
    },

    getRedeemCouponText(campaign = {}) {
      if (campaign.daysActive.length < 1) {
        return '-';
      }

      if (campaign.daysActive.length === 7) {
        return `${this.$t('cms.campaign.Every')} ${this.$t('cms.campaign.Day')}`;
      }

      // Make sure the weekdays are always in the right order
      let sortedDays = this.$CONSTANTS.weekDays.filter(
        weekday => campaign.daysActive.includes(weekday.number),
      );

      // If there are to many days to fit on one line, make the names shorter
      // Ad create sentence to place on button instead of array
      let sortedDayNames = '';

      if (sortedDays.length > 2) {
        sortedDays = sortedDays.map(day => `${day.name.substr(0, 3)}.`);
        sortedDayNames = `${this.$t('cms.campaign.Every')} ${sortedDays.join(' ')}`;
      } else {
        sortedDays = sortedDays.map(day => day.name);
        sortedDayNames = `${this.$t('cms.campaign.Every')} ${sortedDays.join(', ')}`;
      }

      return sortedDayNames;
    },
  },
};
</script>

<style lang="scss">
  .coupon_carousel {
    height: $height_coupon_image;

    .VueCarousel-wrapper{
      height: $height_coupon_image;
    }

    .VueCarousel-pagination {
      z-index: 15;
      display: unset !important;
    }

    .VueCarousel-dot-container {
      margin-top: -30px !important;
    }
  }
</style>

<style lang="scss">
  .text_field {
    * {
      font-size: $font_size_m !important;
    }
  }
</style>

<style lang="scss" scoped>
  .hidden {
    opacity: 0 !important;
  }

  .coupon {
    background-color: $color_background_white;
  }

  .save_button {
    display: flex;
    flex-direction: row;
    flex-grow: 1;
    align-items: center;
    background-color: unset;
    border: unset;
    padding: 0;
  }

  .image_rounding {
    position: absolute;
    background-color: white;
    margin-top: -30px;
    width: $app_screen_width;
    max-width: 100%;
    z-index: 14;
    height: 30px;
    border-radius: 12px 12px 0 0;
  }

  .image_overlay {
    z-index: 99;
    height: $height_coupon_image;
    margin-top: $negative_height_coupon_image;
    width: 100%;
    opacity: .2;
    background: black;
    background: -moz-linear-gradient(0deg, black 0%, transparent 50%);
    background: -webkit-linear-gradient(0deg, black 0%, transparent 50%);
    background: linear-gradient(0deg, black 0%, transparent 50%);
  }

  .image {
    height: $height_coupon_image;
    width: $width_coupon_image;
    position: relative;
    z-index: -1;
  }

  .header {
    font-size: $font_size_l;
    padding-right: $base_spacing * 2;
    padding-left: $base_spacing * 2;
  }

  .text_field {
    padding-right: $base_spacing * 2;
    padding-left: $base_spacing * 2;
    margin-bottom: $base_spacing * 2;
    min-height: $min_height_coupon_text_field;
    font-size: $font_size_m + 3 !important;
  }

  .price {
    position: absolute;
    z-index: 20;
    font-weight: bold;
    display: flex;
    justify-content: center;
    align-content: center;
    align-items: center;
    color: $color_white;
    background-color: $color_good;
    border-radius: 50%;
    height: 60px;
    width: 60px;
    margin: unset;
  }

  .previous_price {
    position: absolute;
    width: 60px;
    text-align: center;
    margin-top: 75px;

    span {
      font-size: $font_size_l;
      color: $color_gray_text;
      text-decoration: line-through;
    }
  }

  .counterdown_section {
    margin-top: -6px;
    text-align: center;
  }

  .maps_link {
    height: 150px;
    position: relative;
    display: block;
  }

  .iframe {
    width: 100%;
    height: 150px;
    margin-top: -150px;
    border: 0;
  }

  .footer {
    z-index: 20;
    margin-top: -4px;
    padding: $base_spacing $base_spacing * 4;
    margin-bottom: 0;

    * {
      color: $color_white;
    }

    background-color: $color_black;

    display: flex;
    flex-direction: row;
    justify-content: space-between;
  }

  .coupon_stats {
    margin: -18px 0;
    width: 100%;
    background-color: $color_background_slightly_yellow;
  }

  .separator_vertical {
    border-right: solid $color_hr 1px;
    display: inline-block;
  }

  .metric_number {
    font-size: 26px;
  }

  .metric_text {
    margin-top: $base_spacing;
    font-size: 12px;
  }
</style>
